import type { ZodErrorMap, ZodIssueCode } from "zod"

export const SSN_ERROR_MAPPING = {
  US: "Please enter a valid Social Security Number (SSN).",
  CA: "Please enter a valid Social Insurance Number (SIN).",
  GB: "Please enter a valid National Insurance Number (NIN).",
  AU: "Please enter a valid Tax File Number (TFN).",
}

/**
 * What one field is told when it fails: a single sentence for every failure,
 * or one per issue code where a single sentence cannot cover both "you left
 * this blank" and "that value is out of range".
 */
type FieldMessage = string | Partial<Record<ZodIssueCode, string>>

/**
 * An error map that gives named fields our own wording, keyed by the dotted
 * path to the field, and leaves every other issue to Zod's default message.
 */
const fieldErrors =
  (messages: Record<string, FieldMessage>): ZodErrorMap =>
  (issue, ctx) => {
    const message = messages[issue.path.join(".")]
    if (typeof message === "string") return { message }
    return { message: message?.[issue.code] ?? ctx.defaultError }
  }

export const businessDetailsErrorMap = fieldErrors({
  legalName: "Please enter your legal business name.",
  "businessDetails.typeOfBusiness": "Please select your type of business.",
  "businessDetails.businessModel": "Please select your business model.",
  "businessDetails.industry": "Please select your industry.",
  "businessDetails.websiteUrl": "Please enter a valid website URL.",
  "businessDetails.startDate": "Please select your incorporation date.",
  "businessDetails.estimatedRevenue":
    "Please select your monthly average revenue.",
})

export const businessAddressErrorMap = fieldErrors({
  incorporatedCountry: "Please select your country.",
  incorporatedState: "Please enter your state or province.",
  incorporatedCity: "Please enter your city.",
  incorporatedZipCode: "Please enter a valid zip or postal code.",
  incorporatedPhone: "Please enter a valid phone number.",
  incorporatedAddress: "Please enter your street address.",
  physicalCountry: "Please select your country.",
  physicalState: "Please enter your state or province.",
  physicalCity: "Please enter your city.",
  physicalZipCode: "Please enter a valid zip or postal code.",
  physicalPhone: "Please enter a valid phone number.",
  physicalAddress: "Please enter your street address.",
})

export const ownershipDetailsErrorMap = fieldErrors({
  firstName: "Please enter your first name.",
  lastName: "Please enter your last name.",
  title: "Please enter your title.",
  dateOfBirth: "Please enter a valid date of birth.",
  country: "Please select your country.",
  state: "Please enter your state or province.",
  city: "Please enter your city.",
  address: "Please enter your street address.",
  zipCode: "Please enter a valid zip or postal code.",
  citizenship: "Please select your citizenship.",
  creditScore: "Please enter your credit score.",
  phone: "Please enter a valid phone number.",
  email: "Please enter a valid email.",
})

export const onboardingBusinessErrorMap = fieldErrors({
  industry: "Please select your industry.",
  businessModel: "Please select how you sell.",
  estimatedRevenue: "Please select your revenue range.",
})

export const onboardingFundingErrorMap = fieldErrors({
  // Left blank and out of range are different mistakes: one sentence covering
  // both tells someone who typed a huge number to type a number.
  capitalAmount: {
    invalid_type: "Please enter how much funding you need.",
    too_small: "Please enter how much funding you need.",
    too_big: "Please enter a smaller amount.",
  },
  useOfFunds: "Please select what the funding is for.",
  whenFundsNeeded: "Please select when you need it.",
})
